import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-muted-foreground">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="space-y-8">
            <Card className="border-primary/50 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full font-medium">BETA</span>
                  Beta Phase Data Practices
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  <strong>Airing Down is currently in Beta.</strong> During this phase, please be aware of the following 
                  regarding your data:
                </p>
                <ul>
                  <li><strong>Evolving Practices:</strong> Our data handling practices may evolve as we develop and improve the platform. We will update this policy to reflect significant changes.</li>
                  <li><strong>Data Resets:</strong> In some cases, user data may be deleted or reset during major platform updates. We will make reasonable efforts to notify users before any planned resets.</li>
                  <li><strong>Backup Recommendation:</strong> We strongly recommend maintaining your own copies of important GPX files and data outside of the platform.</li>
                  <li><strong>Privacy Controls:</strong> Privacy settings and controls are still in development and may change or expand over time.</li>
                </ul>
                <p>
                  Your participation in the Beta helps us build a better platform. We appreciate your understanding 
                  as we work to improve Airing Down.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Information We Collect</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>We collect information you provide directly to us, such as:</p>
                <ul>
                  <li>Account information (email, username)</li>
                  <li>Profile information (display name, bio, preferences)</li>
                  <li>Content you share (GPX files, photos, trail reviews, comments)</li>
                  <li>Communication with us (support requests, feedback)</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Location Data</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  When you upload GPX files or use location features, we collect and process 
                  geographic coordinates and trail data. This information is used to:
                </p>
                <ul>
                  <li>Display your runs and routes on maps</li>
                  <li>Provide trail recommendations</li>
                  <li>Generate analytics and insights</li>
                  <li>Improve our trail database</li>
                </ul>
                <p>
                  You can control the privacy of your location data through your account settings.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>How We Use Your Information</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>We use the information we collect to:</p>
                <ul>
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process transactions and send related information</li>
                  <li>Send technical notices, updates, and support messages</li>
                  <li>Respond to your comments, questions, and requests</li>
                  <li>Analyze usage patterns to improve user experience</li>
                  <li>Prevent fraud and ensure platform security</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Aggregated and Anonymous Data</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  We create aggregated, anonymized datasets from user activity to improve our services 
                  and provide community insights. This includes:
                </p>
                <ul>
                  <li>Trail popularity and usage statistics</li>
                  <li>Aggregated difficulty ratings and condition reports</li>
                  <li>Seasonal usage patterns and trends</li>
                  <li>Equipment and vehicle type distributions</li>
                </ul>
                <p>
                  <strong>Your Privacy is Protected:</strong> Aggregated data is stripped of personally 
                  identifiable information. Individual run tracks marked as private or shared only with 
                  clubs are never exposed—only their contribution to aggregate counts and statistics 
                  (e.g., "15 runs this month") is included.
                </p>
                <p>We may share aggregated insights with:</p>
                <ul>
                  <li>Land management agencies to support trail maintenance</li>
                  <li>The off-road community through public dashboards</li>
                  <li>Research partners studying outdoor recreation patterns</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Information Sharing</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>We do not sell, trade, or rent your personal information. We may share information:</p>
                <ul>
                  <li>With other users when you choose to make content public</li>
                  <li>With service providers who assist in our operations</li>
                  <li>When required by law or to protect our rights</li>
                  <li>In connection with a business transfer or acquisition</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Security</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  We implement appropriate security measures to protect your personal information 
                  against unauthorized access, alteration, disclosure, or destruction. However, 
                  no method of transmission over the internet is 100% secure.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Your Rights</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>You have the right to:</p>
                <ul>
                  <li>Access, update, or delete your personal information</li>
                  <li>Control the privacy settings of your content</li>
                  <li>Export your data in a portable format</li>
                  <li>Object to certain processing of your information</li>
                  <li>Delete your account and associated data</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cookies and Tracking</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  We use cookies and similar technologies to enhance your experience, analyze usage, 
                  and remember your preferences. You can control cookie settings through your browser.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contact Us</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-neutral dark:prose-invert max-w-none">
                <p>
                  If you have questions about this Privacy Policy or our data practices, 
                  please reach out through our <a href="/contact" className="text-primary hover:underline">Contact page</a> or 
                  email us at support@airingdown.com.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Privacy;